import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userProfileService } from '../services/userProfileService';
import { UserProfile } from '../types/user';
import { useAuth } from '../contexts/AuthContext';
import { offlineCache } from '../services/offlineCache';
import { networkService } from '../services/network';

export const userProfileKeys = {
  all: ['userProfile'] as const,
};

/**
 * Fetch user profile with offline support.
 * Tries API first, caches response, falls back to offline cache if unavailable.
 */
async function fetchProfileWithOfflineSupport(): Promise<UserProfile> {
  const isOnline = networkService.isConnected();

  if (isOnline) {
    try {
      const profile = await userProfileService.getProfile();
      // Cache successful API response for offline use
      await offlineCache.cacheUserProfile(profile);
      return profile;
    } catch (error) {
      console.warn('Failed to fetch profile from API, trying offline cache:', error);
      // Fall through to offline cache
    }
  }

  // Offline or API failed: try cached data
  const cachedProfile = await offlineCache.getCachedUserProfile();
  if (cachedProfile) {
    return cachedProfile as UserProfile;
  }

  // No cached data available - return empty profile
  return { userRole: '', phone: '' };
}

export const useUserProfile = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const {
    data: profile,
    isLoading,
    error,
  } = useQuery({
    queryKey: userProfileKeys.all,
    queryFn: fetchProfileWithOfflineSupport,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours for offline support
    networkMode: 'always', // Allow query to run even when offline
    placeholderData: (previousData) => previousData, // Keep previous data during refetch
    refetchOnReconnect: true, // Refetch when connection restored
    retry: false, // Don't retry on failure - offline fallback handles it
  });

  const mutation = useMutation({
    mutationFn: (data: Partial<UserProfile>) =>
      userProfileService.updateProfile(data),
    networkMode: 'always', // Allow mutations to run when online check happens in screen
    onSuccess: async (updatedProfile) => {
      // Update cache immediately with new data
      queryClient.setQueryData(userProfileKeys.all, updatedProfile);
      // Also persist to offline cache
      await offlineCache.cacheUserProfile(updatedProfile);
    },
  });

  return {
    profile: profile ?? null,
    isLoading,
    updateProfile: mutation.mutate,
    updateProfileAsync: mutation.mutateAsync,
    isSaving: mutation.isPending,
    error: error ?? mutation.error ?? null,
  };
};
